import React, { useState } from 'react';
import { useContactForm } from '../hooks/useApi';
import { sendMail } from '../lib/api';

type ServiceOption = { label: string; value: string };

interface FormData {
  name: string;
  company: string;
  phone: string;
  email: string;
  description: string;
  services: string[];
}

interface FormErrors {
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
  services?: string;
}

const webServices: ServiceOption[] = [
  { label: 'Website Design & Development', value: 'Website Design & Development' },
  { label: 'Ecommerce Development', value: 'Ecommerce' },
  { label: 'Mobile APP Development', value: 'Mobile APP Development' },
  { label: 'Custom Web Applications', value: 'Custom Web Applications' },
];

const digitalMarketing: ServiceOption[] = [
  { label: 'SEO : Search Engine Optimisation', value: 'SEO : Search Engine Optimisation' },
  { label: 'SMO : Social Media Optimisation', value: 'SMO : Social Media Optimisation' },
  { label: 'SEM : Search Engine Marketing', value: 'SEM : Search Engine Marketing' },
];

const mediaServices: ServiceOption[] = [
  { label: 'Interactive Presentations', value: 'Interactive Presentations' },
  { label: 'Video Services', value: 'Video Services' },
  { label: 'Branding Services', value: 'Branding Services' },
];

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    company: '',
    phone: '',
    email: '',
    description: '',
    services: []
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [submitMessage, setSubmitMessage] = useState('');

  // Alternative: You can use the useContactForm hook instead of manual state management
  // const { loading, error, success, submitContactForm, reset } = useContactForm();

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle checkbox changes
  const handleServiceChange = (serviceValue: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      services: checked 
        ? [...prev.services, serviceValue]
        : prev.services.filter(s => s !== serviceValue)
    }));
    
    // Clear services error when user selects a service
    if (errors.services) {
      setErrors(prev => ({
        ...prev,
        services: undefined
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.services.length === 0) {
      newErrors.services = 'Please select at least one service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Form submission started');
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    
    console.log('Form validation passed, proceeding with submission');

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare data for API (matching the expected format)
      const apiData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: `Quote Request - ${formData.services.join(', ')}`,
        message: `Company: ${formData.company}\n\nServices Requested:\n${formData.services.join('\n')}\n\nDescription:\n${formData.description || 'No additional description provided.'}`,
        company: formData.company,
        services: formData.services,
        description: formData.description
      };

      console.log('Sending contact form data:', apiData);
      
      // Use the dynamic API service
      const result = await sendMail(apiData);

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage(result.message || 'Thank you! Your quote request has been sent successfully.');
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          description: '',
          services: []
        });
      } else {
        throw new Error(result.error || result.message || 'Failed to send quote request');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      
      // Check for specific error types
      let errorMessage = 'Server error, please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('CSP') || error.message.includes('Content Security Policy')) {
          errorMessage = 'Security policy error. Please try refreshing the page or contact us directly at admin@intenseinfotech.com';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setSubmitMessage(errorMessage + ' You can also email us directly at admin@intenseinfotech.com');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section id="contact" className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-900">Please fill the form below:</h2>
        </div>

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div className="max-w-5xl mx-auto mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
              </svg>
              {submitMessage}
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="max-w-5xl mx-auto mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div>{submitMessage}</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <input 
                type="text" 
                name="name"
                placeholder="Name" 
                value={formData.name}
                onChange={handleInputChange}
                className={`border p-3 rounded w-full ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                required 
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            
            <div>
              <input 
                type="text" 
                name="company"
                placeholder="Company Name" 
                value={formData.company}
                onChange={handleInputChange}
                className={`border p-3 rounded w-full ${errors.company ? 'border-red-500' : 'border-gray-300'}`}
                required 
              />
              {errors.company && <p className="mt-1 text-sm text-red-600">{errors.company}</p>}
            </div>
            
            <div>
              <input 
                type="tel" 
                name="phone"
                placeholder="Mobile Number" 
                value={formData.phone}
                onChange={handleInputChange}
                className={`border p-3 rounded w-full ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                required 
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>
            
            <div>
              <input 
                type="email" 
                name="email"
                placeholder="Email Address" 
                value={formData.email}
                onChange={handleInputChange}
                className={`border p-3 rounded w-full ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                required 
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>
          </div>

          <div className="mt-6">
            <textarea 
              name="description"
              placeholder="Description" 
              rows={3} 
              value={formData.description}
              onChange={handleInputChange}
              className="border border-gray-300 p-3 rounded w-full"
            />
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h5 className="text-lg font-semibold text-blue-800 mb-3">Web</h5>
              <div className="space-y-2">
                {webServices.map((s) => (
                  <label key={s.value} className="flex items-center gap-2 text-gray-700">
                    <input 
                      type="checkbox" 
                      value={s.value} 
                      checked={formData.services.includes(s.value)}
                      onChange={(e) => handleServiceChange(s.value, e.target.checked)}
                      className="w-4 h-4" 
                    />
                    <span>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-blue-800 mb-3">Digital Marketing</h5>
              <div className="space-y-2">
                {digitalMarketing.map((s) => (
                  <label key={s.value} className="flex items-center gap-2 text-gray-700">
                    <input 
                      type="checkbox" 
                      value={s.value} 
                      checked={formData.services.includes(s.value)}
                      onChange={(e) => handleServiceChange(s.value, e.target.checked)}
                      className="w-4 h-4" 
                    />
                    <span>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-blue-800 mb-3">Media Services</h5>
              <div className="space-y-2">
                {mediaServices.map((s) => (
                  <label key={s.value} className="flex items-center gap-2 text-gray-700">
                    <input 
                      type="checkbox" 
                      value={s.value} 
                      checked={formData.services.includes(s.value)}
                      onChange={(e) => handleServiceChange(s.value, e.target.checked)}
                      className="w-4 h-4" 
                    />
                    <span>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {errors.services && (
            <div className="mt-2 text-center">
              <p className="text-sm text-red-600">{errors.services}</p>
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={`px-10 py-3 rounded shadow text-lg font-semibold transition ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed text-blue-900' 
                  : 'bg-cyan-400 hover:bg-cyan-300 text-blue-900'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
